import { html } from 'lit';
import { editorSemanticText } from '../editor/editorSemanticText.js';
import {
  editorNodeClass,
  editorNodeClick,
  editorNodePointerDown,
  editorNodeStyle,
  type LandingEditorRenderOptions,
} from '../editor/landingEditorRender.js';

interface HighlightedHeadingOptions {
  title: string;
  headingTag?: 'h1' | 'h2';
  highlight?: string;
  className?: string;
  editor?: LandingEditorRenderOptions;
  titleElementId?: string;
  highlightElementId?: string;
}

export interface HeadingHighlightParts {
  before: string;
  highlight: string;
  after: string;
}

class HeadingHighlightPresenter {
  constructor(
    private readonly title: string,
    private readonly highlight?: string,
  ) {}

  get parts(): HeadingHighlightParts {
    const explicitMatch = this.explicitHighlightMatch();
    if (explicitMatch) return explicitMatch;
    return this.fallbackLastWordMatch();
  }

  private explicitHighlightMatch(): HeadingHighlightParts | null {
    const needle = this.highlight?.trim();
    if (!needle) return null;

    const index = this.title.toLocaleLowerCase().lastIndexOf(needle.toLocaleLowerCase());
    if (index < 0) return null;

    return {
      before: this.title.slice(0, index),
      highlight: this.title.slice(index, index + needle.length),
      after: this.title.slice(index + needle.length),
    };
  }

  private fallbackLastWordMatch(): HeadingHighlightParts {
    const match = /(\S+)(\s*)$/.exec(this.title);
    if (!match?.[1]) return { before: this.title, highlight: '', after: '' };
    const index = this.title.lastIndexOf(match[1]);
    return {
      before: this.title.slice(0, index),
      highlight: match[1],
      after: this.title.slice(index + match[1].length),
    };
  }
}

export const combineHeadingTitle = (title: string, highlight?: string) => {
  const normalizedHighlight = highlight?.trim();
  if (!normalizedHighlight) return title;
  if (title.toLocaleLowerCase().includes(normalizedHighlight.toLocaleLowerCase())) return title;
  return `${title} ${normalizedHighlight}`;
};

export const splitHeadingHighlight = (title: string, highlight?: string): HeadingHighlightParts =>
  new HeadingHighlightPresenter(title, highlight).parts;

export const highlightedHeading = ({
  title,
  headingTag = 'h1',
  highlight,
  className = '',
  editor,
  titleElementId,
  highlightElementId,
}: HighlightedHeadingOptions) => {
  const parts = splitHeadingHighlight(title, highlight);
  const highlightClass = highlightElementId
    ? editorNodeClass(editor, highlightElementId, 'vp-heading-highlight')
    : 'vp-heading-highlight';
  const highlightStyle = highlightElementId ? editorNodeStyle(editor, highlightElementId) : '';

  const content = html`${parts.before}${parts.highlight
    ? html`<span
        class=${highlightClass}
        style=${highlightStyle}
        @pointerdown=${(event: PointerEvent) =>
          highlightElementId && editorNodePointerDown(editor, highlightElementId, event)}
        @click=${(event: MouseEvent) => highlightElementId && editorNodeClick(editor, highlightElementId, event)}
        >${parts.highlight}</span
      >`
    : ''}${parts.after}`;
  return editorSemanticText({
    editor,
    elementId: titleElementId,
    fallbackTag: headingTag,
    className: `vp-highlight-heading ${className}`,
    content,
  });
};
